import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export type GenderOption = 'male' | 'female';

export interface WhoopProfile {
  userId: number;
  email: string;
  firstName: string;
  lastName: string;
}

export interface WhoopBodyMeasurement {
  heightMeter: number;
  weightKilogram: number;
  maxHeartRate?: number;
}

export interface WhoopExchangeResponse {
  connected: boolean;
  profile?: WhoopProfile;
  bodyMeasurement?: WhoopBodyMeasurement;
  tokenExpiresIn?: number;
  scope?: string;
}

export interface WhoopRecalibrationSummary {
  bmi: number;
  bodyFatPercent: number;
  usedEstimatedBodyFat: boolean;
  leanMassLbs: number;
  fatMassLbs: number;
  recalibrationFactor: number;
  heightFeet: number;
  heightInches: number;
  weightLbs: number;
}

export interface WhoopWorkoutItem {
  id: string;
  sportName: string;
  startedAt: string;
  endedAt: string;
  distanceMiles: number;
  strain: number;
  whoopKilojoules: number;
  standardCalories: number;
  adjustedCalories: number;
  usedWhoopKilojoules: boolean;
}

export interface WhoopWorkoutTotals {
  workoutCount: number;
  standardCalories: number;
  adjustedCalories: number;
}

export interface WhoopWorkoutResponse {
  connected: boolean;
  profile?: WhoopProfile;
  bodyMeasurement?: WhoopBodyMeasurement;
  recalibration?: WhoopRecalibrationSummary;
  workouts: WhoopWorkoutItem[];
  totals: WhoopWorkoutTotals;
}

@Injectable({
  providedIn: 'root'
})
export class WhoopCalibrationService {

  private readonly apiBaseUrl = 'https://43bol7a6gwgstatsgges7tf6tu0pfrwy.lambda-url.us-east-1.on.aws/';

  constructor(private http: HttpClient) {}

  getBackendOrigin(): string {
    try {
      return new URL(this.apiBaseUrl).origin;
    } catch {
      return window.location.origin;
    }
  }

  getAuthUrl(appUserId: string, origin: string): Observable<{ authUrl: string; state: string }> {
    return this.http.get<{ authUrl: string; state: string }>(
      `${this.apiBaseUrl}/whoop/auth-url`,
      {
        params: {
          appUserId,
          origin
        }
      }
    );
  }

  exchangeCode(payload: {
    appUserId: string;
    code: string;
    state: string;
  }): Observable<WhoopExchangeResponse> {
    return this.http.post<WhoopExchangeResponse>(
      `${this.apiBaseUrl}/whoop/exchange`,
      payload
    );
  }

  recalculateWhoopWorkouts(payload: {
    appUserId: string;
    age: number;
    gender: GenderOption;
    bodyFatPercent?: number | null;
    limit?: number;
  }): Observable<WhoopWorkoutResponse> {
    return this.http.post<WhoopWorkoutResponse>(
      `${this.apiBaseUrl}/whoop/workouts/recalculate`,
      payload
    );
  }

  disconnectWhoop(appUserId: string): Observable<{ disconnected: boolean }> {
    return this.http.delete<{ disconnected: boolean }>(
      `${this.apiBaseUrl}/whoop/disconnect`,
      {
        params: { appUserId }
      }
    );
  }
}